import React, { useState, useEffect } from 'react';
import { Bar, Pie, Line, Doughnut, PolarArea, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register the components with Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, LineElement, RadialLinearScale, PointElement, Title, Tooltip, Legend);

  const ChartComponent = ({ data, flag, responseType }) => {
  // const [showBarChart, setShowBarChart] = useState(!flag);
  const [chartType, setChartType] = useState('bar');

  // const toggleChart = () => {
  //   setShowBarChart(!showBarChart);
  // };
  const chartTypes = ['bar', 'pie', 'line', 'doughnut', 'polarArea', 'radar'];
  const toggleChart = () => {
    const currentIndex = chartTypes.indexOf(chartType);
    const nextIndex = (currentIndex + 1) % chartTypes.length;
    setChartType(chartTypes[nextIndex]);
  };

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            family: 'Arial, sans-serif',
          },
          boxWidth: 20,
        },
      },
      title: {
        display: true,
        text: responseType === 'ranking' ? 'Ranking Order Distribution' : 'Responses Count',
        font: {
          size: 16,
          family: 'Arial, sans-serif',
        },
        color: '#333',
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            const label = context[0].label;
            // Show original full label in tooltip title
            return data.originalLabels && data.originalLabels[label] ? data.originalLabels[label] : label;
          },
          label: function(context) {
            if (responseType === 'ranking') {
              return `${context.parsed.y} users chose this ranking order`;
            }
            return `Count: ${context.parsed.y}`;
          }
        },
        titleFont: {
          size: 12,
          weight: 'bold'
        },
        bodyFont: {
          size: 11
        },
        maxWidth: 400,
        displayColors: true,
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10
          },
          callback: function(value, index, values) {
            const label = this.getLabelForValue(value);
            // Truncate label for x-axis display
            return label.length > 20 ? label.substring(0, 20) + '...' : label;
          }
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: {
            size: 10
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 10,
            family: 'Arial, sans-serif',
          },
          boxWidth: 15,
          padding: 10,
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, index) => {
                const dataset = data.datasets[0];
                const value = dataset.data[index];
                const total = dataset.data.reduce((sum, val) => sum + val, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                
                return {
                  text: `${label.length > 25 ? label.substring(0, 25) + '...' : label} (${percentage}%)`,
                  fillStyle: dataset.backgroundColor[index],
                  strokeStyle: dataset.borderColor[index],
                  lineWidth: dataset.borderWidth,
                  hidden: false,
                  index: index
                };
              });
            }
            return [];
          }
        },
      },
      title: {
        display: true,
        text: responseType === 'ranking' ? 'Ranking Order Distribution' : 'Responses Count',
        font: {
          size: 16,
          family: 'Arial, sans-serif',
        },
        color: '#333',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function(context) {
            const label = context[0].label;
            // Show original full label in tooltip title
            return data.originalLabels && data.originalLabels[label] ? data.originalLabels[label] : label;
          },
          label: function(context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            if (responseType === 'ranking') {
              return `${value} users (${percentage}%) chose this ranking order`;
            }
            return `Count: ${value} (${percentage}%)`;
          }
        },
        titleFont: {
          size: 12,
          weight: 'bold'
        },
        bodyFont: {
          size: 11
        },
        maxWidth: 400,
      }
    },
  };

  const containerStyle = {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '20px',
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
  };

  const buttonStyle = {
    backgroundColor:'#1ab394', // main color
    color: 'white',
    padding: '6px 27px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    margin: '10px 10px',
  };

  const vividColors = [
    '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#E7E9ED',
    '#FF5733', '#33FF57', '#3357FF', '#FF33F1', '#F1FF33', '#33FFF1', '#F133FF',
    '#FF8C00', '#32CD32', '#8A2BE2', '#FF1493', '#00CED1', '#FF4500', '#DA70D6',
  ];

    // Helper function to shuffle an array
    const shuffleArray = (array) => {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    // Randomize the colors for each chart render
    const randomizedColors = shuffleArray(vividColors).slice(0, data.labels.length);

    const updatedData = {
      ...data,
      datasets: data.datasets.map(dataset => ({
        ...dataset,
        backgroundColor: randomizedColors,
        borderColor: randomizedColors.map(color => darkenColor(color, 0.2)),
        borderWidth: 1,
      })),
    };


  return (
    <div style={containerStyle}>
      {/* <button onClick={toggleChart} style={buttonStyle}>
        {showBarChart ? 'Show Pie Chart' : 'Show Bar Chart'}
      </button> */}
      <button onClick={toggleChart} style={buttonStyle}>
        Switch to {chartTypes[(chartTypes.indexOf(chartType) + 1) % chartTypes.length].toUpperCase()} Chart
      </button>

      {/* <div style={{ padding: '20px' }}>
        {showBarChart ? (
          <div style={{ height: '500px', width: '100%' }}>
            <Bar data={updatedData} options={commonOptions} />
          </div>
        ) : (
          <div style={{ height: '400px', width: '105%' }}>
            <Pie data={updatedData} options={pieOptions} />
          </div>
        )}
      </div> */}
      <div style={{ padding: '20px' }}>
        {chartType === 'bar' && (
          <div style={{ height: '400px', width: '100%' }}>
            <Bar data={updatedData} options={commonOptions} />
          </div>
        )}
        {chartType === 'pie' && (
          <div style={{ height: '400px', width: '100%' }}>
            <Pie data={updatedData} options={pieOptions} />
          </div>
        )}
        {chartType === 'line' && (
          <div style={{ height: '400px', width: '100%' }}>
            <Line data={updatedData} options={commonOptions} />
          </div>
        )}
        {chartType === 'doughnut' && (
          <div style={{ height: '400px', width: '100%' }}>
            <Doughnut data={updatedData} options={pieOptions} />
          </div>
        )}
        {chartType === 'polarArea' && (
          <div style={{ height: '400px', width: '100%' }}>
            <PolarArea data={updatedData} options={pieOptions} />
          </div>
        )}
        {chartType === 'radar' && (
          <div style={{ height: '400px', width: '100%' }}>
            <Radar data={updatedData} options={commonOptions} />
          </div>
        )}
      </div>
    </div>
  );
};

// Utility function to darken a color
const darkenColor = (color, amount) => {
  let usePound = false;

  if (color[0] === "#") {
    color = color.slice(1);
    usePound = true;
  }

  let num = parseInt(color, 16);

  let r = (num >> 16) + amount;
  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let g = ((num >> 8) & 0x00FF) + amount;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  let b = (num & 0x0000FF) + amount;
  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  return (usePound ? "#" : "") + (r.toString(16).padStart(2, '0')) + (g.toString(16).padStart(2, '0')) + (b.toString(16).padStart(2, '0'));
};

export default ChartComponent;
